import { useState } from "react";
import { useParams } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Check, Flag, Pencil, X } from "lucide-react";
import { TenantLayout, useTenant } from "@/components/tenant/tenant-layout";
import { useAuth } from "@/lib/auth/auth-provider";
import { useRole } from "@/lib/auth/use-role";
import {
  Badge,
  Button,
  Card,
  ConfidenceChip,
  PageHeader,
  Spinner,
} from "@/components/ui/primitives";
import {
  acceptSection,
  getDocumentByType,
  listSections,
  saveSection,
  type DocumentType,
} from "@/lib/api/manuals";
import {
  APPLICABILITY_LABEL,
  APPLICABILITY_TONE,
  SECTION_REVIEW_LABEL,
  SECTION_REVIEW_TONE,
  SECTION_SOURCE_HINT,
  SECTION_SOURCE_LABEL,
  SECTION_SOURCE_TONE,
} from "@/lib/manual-labels";
import { DOC_UI, DocHomeLink, DocSectionLink } from "@/pages/tenant/doc-nav";
import type { SectionApplicability } from "@argus/engine";

/** Render the body with `[CCO: …]` gap markers highlighted as the firm-specific
 * inputs the CCO still owes — the product's honesty mechanic, made visible. */
function RenderedBody({ body }: { body: string }) {
  const parts = body.split(/(\[CCO:[^\]]*\])/g);
  return (
    <div className="whitespace-pre-wrap text-sm leading-relaxed text-neutral-200">
      {parts.map((part, i) =>
        part.startsWith("[CCO:") ? (
          <mark
            key={i}
            className="rounded bg-amber-950/50 px-1 text-amber-200 ring-1 ring-amber-900/60"
          >
            {part}
          </mark>
        ) : (
          <span key={i}>{part}</span>
        ),
      )}
    </div>
  );
}

function SectionInner({ sectionKey, docType }: { sectionKey: string; docType: DocumentType }) {
  const tenant = useTenant();
  const { user } = useAuth();
  const qc = useQueryClient();
  const { canWrite, role } = useRole(tenant.id);
  const canEdit = canWrite || role === "reviewer";

  const { data: doc } = useQuery({
    queryKey: ["doc", docType, tenant.id],
    queryFn: () => getDocumentByType(tenant.id, docType),
  });
  const { data: sections, isLoading } = useQuery({
    queryKey: ["doc-sections", doc?.id],
    queryFn: () => listSections(doc?.id ?? ""),
    enabled: !!doc,
  });

  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");

  function invalidate() {
    if (doc) {
      void qc.invalidateQueries({ queryKey: ["doc-sections", doc.id] });
      void qc.invalidateQueries({ queryKey: ["doc-events", doc.id] });
    }
  }

  const ordered = sections ?? [];
  const idx = ordered.findIndex((s) => s.section_key === sectionKey);
  const section = idx >= 0 ? ordered[idx] : undefined;
  const prev = idx > 0 ? ordered[idx - 1] : undefined;
  const next = idx >= 0 && idx < ordered.length - 1 ? ordered[idx + 1] : undefined;

  const save = useMutation({
    mutationFn: (patch: Parameters<typeof saveSection>[1]) => {
      if (!user || !section) throw new Error("Not ready.");
      return saveSection(section, patch, user.id);
    },
    onSuccess: invalidate,
    onError: (e: Error) => toast.error("Save failed", { description: e.message }),
  });
  const accept = useMutation({
    mutationFn: () => {
      if (!user || !section) throw new Error("Not ready.");
      return acceptSection(section, user.id);
    },
    onSuccess: () => {
      invalidate();
      toast.success("Section accepted.");
    },
  });

  if (isLoading || !doc) return <p className="text-sm text-neutral-500">Loading section…</p>;
  if (!section)
    return (
      <div className="rounded border border-red-900/60 bg-red-950/30 px-3 py-2 text-xs text-red-300">
        Section not found.
      </div>
    );

  const hasBody = !!section.body && section.body.trim().length > 0;
  const naApplicable = section.applicability === "not_applicable";

  return (
    <>
      <DocHomeLink
        docType={docType}
        slug={tenant.slug}
        className="inline-flex items-center gap-1.5 text-xs text-neutral-500 hover:text-neutral-300"
      >
        <ArrowLeft className="size-3.5" /> {DOC_UI[docType].title}
      </DocHomeLink>

      <PageHeader
        title={section.heading}
        subtitle={
          <span className="flex flex-wrap items-center gap-1.5">
            {section.reg_ref && <Badge tone="neutral">{section.reg_ref}</Badge>}
            <Badge tone={APPLICABILITY_TONE[section.applicability]}>
              {APPLICABILITY_LABEL[section.applicability]}
            </Badge>
            <Badge tone={SECTION_SOURCE_TONE[section.source]}>
              {SECTION_SOURCE_LABEL[section.source]}
            </Badge>
            {section.confidence != null && hasBody && !naApplicable && (
              <ConfidenceChip value={section.confidence} />
            )}
            {hasBody && (
              <Badge tone={SECTION_REVIEW_TONE[section.review_status]}>
                {SECTION_REVIEW_LABEL[section.review_status]}
              </Badge>
            )}
          </span>
        }
      />

      {canEdit && (
        <Card className="flex flex-wrap items-center gap-2 p-3">
          <span className="mr-1 text-[11px] uppercase tracking-wide text-neutral-600">
            Applies to this firm
          </span>
          {(["applicable", "recommended", "not_applicable"] as SectionApplicability[]).map((a) => (
            <Button
              key={a}
              size="sm"
              variant={section.applicability === a ? "secondary" : "ghost"}
              disabled={save.isPending}
              onClick={() => save.mutate({ applicability: a })}
            >
              {APPLICABILITY_LABEL[a]}
            </Button>
          ))}
        </Card>
      )}

      <Card className="p-5">
        <p className="mb-3 text-[11px] text-neutral-500">{SECTION_SOURCE_HINT[section.source]}</p>

        {editing ? (
          <div className="space-y-3">
            <textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              rows={16}
              className="w-full rounded-md border border-neutral-700 bg-neutral-950 px-3 py-2 text-sm leading-relaxed text-neutral-100"
            />
            <div className="flex items-center gap-2">
              <Button
                variant="success"
                disabled={save.isPending}
                onClick={() =>
                  save.mutate(
                    { body: draft },
                    {
                      onSuccess: () => {
                        setEditing(false);
                        toast.success("Section saved.");
                      },
                    },
                  )
                }
              >
                {save.isPending ? <Spinner className="size-4" /> : <Check className="size-4" />}{" "}
                Save
              </Button>
              <Button variant="ghost" onClick={() => setEditing(false)}>
                <X className="size-4" /> Cancel
              </Button>
            </div>
          </div>
        ) : naApplicable ? (
          <p className="text-sm italic text-neutral-500">
            This section is marked not applicable to your firm and is excluded from the adopted
            document. Mark it applicable above to include and draft it.
          </p>
        ) : hasBody ? (
          <RenderedBody body={section.body ?? ""} />
        ) : (
          <p className="text-sm italic text-neutral-600">
            Not drafted yet. Use “Draft with AI” on the overview, or edit to author it.
          </p>
        )}

        {section.citation && !editing && (
          <p className="mt-4 border-t border-neutral-800/70 pt-3 text-[11px] text-neutral-500">
            {section.citation}
          </p>
        )}

        {canEdit && !editing && !naApplicable && (
          <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-neutral-800/70 pt-4">
            {hasBody && section.review_status === "unreviewed" && (
              <Button
                size="sm"
                variant="success"
                disabled={accept.isPending}
                onClick={() => accept.mutate()}
              >
                <Check className="size-3.5" /> Accept
              </Button>
            )}
            <Button
              size="sm"
              variant="secondary"
              onClick={() => {
                setDraft(section.body ?? "");
                setEditing(true);
              }}
            >
              <Pencil className="size-3.5" /> {hasBody ? "Edit" : "Author"}
            </Button>
            {hasBody && section.review_status !== "flagged" && (
              <Button
                size="sm"
                variant="ghost"
                disabled={save.isPending}
                onClick={() => save.mutate({ reviewStatus: "flagged" })}
                title="Flag for follow-up"
              >
                <Flag className="size-3.5" /> Flag
              </Button>
            )}
          </div>
        )}
      </Card>

      {/* Prev / next section navigation */}
      <div className="flex items-center justify-between">
        {prev ? (
          <DocSectionLink
            docType={docType}
            slug={tenant.slug}
            sectionKey={prev.section_key}
            className="inline-flex items-center gap-1.5 text-xs text-neutral-400 hover:text-neutral-200"
          >
            <ArrowLeft className="size-3.5" /> {prev.heading}
          </DocSectionLink>
        ) : (
          <span />
        )}
        {next ? (
          <DocSectionLink
            docType={docType}
            slug={tenant.slug}
            sectionKey={next.section_key}
            className="inline-flex items-center gap-1.5 text-xs text-neutral-400 hover:text-neutral-200"
          >
            {next.heading} <ArrowRight className="size-3.5" />
          </DocSectionLink>
        ) : (
          <span />
        )}
      </div>
    </>
  );
}

export function ManualSectionPage({ docType = "compliance_manual" }: { docType?: DocumentType }) {
  const { sectionKey } = useParams({ strict: false });
  return (
    <TenantLayout>
      <SectionInner sectionKey={sectionKey ?? ""} docType={docType} />
    </TenantLayout>
  );
}
